import { BreadcrumbGroupProps, SideNavigationProps } from '@amzn/awsui-components-core';
import { ADMIN_LDAP_GROUPS, DEV_LDAP_GROUPS, HUDDLEPLUS_ADMIN_LDAP_GROUPS, KINGPIN_ACCESS_GROUPS, eLDAPGroup } from '../constants/AppConstants';
import { KingPinSideNavLinks, Titles } from 'src/components/das-finsuite/das-finsuite-constants';
import { ENVIRONMENT_VARIABLES, IS_NON_PROD } from '../constants/EnvironmentConstants';

export interface RouteConfig {
  path: string;
  breadcrumbText: string;
  navigationText?: string;
  parent?: string;
  isDynamic?: boolean;
  dynamicTextResolver?: (params: any) => string;
  showInNavigation?: boolean;
  navigationSection?: string;
  accessibleTo?: string[];
  external?: boolean;
  environmentRestriction?: 'non-prod' | 'prod' | 'all';
}

export interface NavigationSection {
  name: string;
  items: RouteConfig[];
}

export const ROUTE_CONFIG: Record<string, RouteConfig> = {
  // Main routes
  '/': {
    path: '/',
    breadcrumbText: 'Home',
    navigationText: 'FinSuite',
    showInNavigation: false // Header item, not in navigation list
  },

  // FinSuite routes - Main Navigation
  '/all-apps': {
    path: '/all-apps',
    breadcrumbText: Titles.TITLE_ALL_APPS,
    navigationText: Titles.TITLE_ALL_APPS,
    parent: '/',
    showInNavigation: true,
    navigationSection: 'finsuite'
  },
  // '/teams': {
  //   path: '/teams',
  //   breadcrumbText: 'DaS FinTech Team',
  //   navigationText: 'DaS FinTech Team',
  //   parent: '/',
  //   showInNavigation: true,
  //   navigationSection: 'finsuite'
  // },
  '/announcements': {
    path: '/announcements',
    breadcrumbText: Titles.TITLES_ANNOUNCEMENTS,
    navigationText: Titles.TITLES_ANNOUNCEMENTS,
    parent: '/',
    showInNavigation: true,
    navigationSection: 'finsuite'
  },
  // '/tutorials': {
  //   path: '/tutorials',
  //   breadcrumbText: 'Tutorials',
  //   navigationText: 'Tutorials',
  //   parent: '/',
  //   showInNavigation: true,
  //   navigationSection: 'finsuite'
  // },
  '/kingpin/goals': {
    path: '/kingpin/goals',
    breadcrumbText: 'Frame',
    navigationText: 'Frame',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'org-tools',
    accessibleTo: KINGPIN_ACCESS_GROUPS
  },
  '/kingpin/insights': {
    path: '/kingpin/insights',
    breadcrumbText: 'Frame',
    navigationText: 'Frame',
    parent: '/',
    showInNavigation: false,
    navigationSection: 'org-tools',
    accessibleTo: KINGPIN_ACCESS_GROUPS
  },
  '/kingpin/charts': {
    path: '/kingpin/charts',
    breadcrumbText: 'Frame',
    navigationText: 'Frame',
    parent: '/',
    showInNavigation: false,
    navigationSection: 'org-tools',
    accessibleTo: KINGPIN_ACCESS_GROUPS
  },
  '/ea-huddle': {
    path: '/ea-huddle',
    breadcrumbText: 'Huddle+',
    navigationText: 'Huddle+',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'org-tools'
  },

  '/baseball-cards': {
    path: '/baseball-cards',
    breadcrumbText: 'Baseball Cards',
    navigationText: 'Baseball Cards',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'org-tools',
    accessibleTo: [eLDAPGroup.BASEBALL_CARDS_LDAP, eLDAPGroup.DEV_LDAP]
  },

  '/docs': {
    path: '/docs',
    breadcrumbText: 'MBR & Roadmap Docs',
    navigationText: 'MBR & Roadmap Docs',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'org-tools'
  },

  '/mcp': {
    path: '/mcp',
    breadcrumbText: 'MCP Servers',
    navigationText: 'MCP Servers',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'org-tools'
  },

  '/canary': {
    path: '/canary',
    breadcrumbText: 'Canary',
    navigationText: 'Canary',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'org-tools',
    accessibleTo: [
      eLDAPGroup.CANARY_SCORECARD_POWERUSERS_LDAP,
      eLDAPGroup.CANARY_SCORECARD_VIEWERS_LDAP,
      // DEV sees the nav link only in non-prod (dev/alpha); hidden in beta/prod to match the route guard.
      ...(IS_NON_PROD ? [eLDAPGroup.DEV_LDAP] : [])
    ]
  },

  // Internal route that embeds the Harmony-hosted Summer Wellness Leaderboard
  // inline (see SummerWellnessTrackerPage). Not external — clicking navigates
  // within the SPA and renders the iframe. Placed last in Org Tools, next to the
  // Operational Excellence entry.
  '/org-tools/wellness-tracker': {
    path: '/org-tools/wellness-tracker',
    breadcrumbText: 'Wellness Tracker',
    navigationText: 'Wellness Tracker',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'org-tools'
  },

  // Admin Console routes
  '/admin': {
    path: '/admin',
    breadcrumbText: 'Quick Actions',
    navigationText: 'Quick Actions',
    parent: '/',
    showInNavigation: false // Header item
  },
  '/admin/applications': {
    path: '/admin/applications',
    breadcrumbText: 'All Applications',
    navigationText: 'Apps',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/manage-applications': {
    path: '/admin/manage-applications',
    breadcrumbText: 'Manage Applications',
    navigationText: 'Add New App',
    parent: '/admin/applications',
    showInNavigation: true,
    navigationSection: 'admin-new-app',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/announcements': {
    path: '/admin/announcements',
    breadcrumbText: 'Announcements',
    navigationText: 'Product Launches',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/feedbacks': {
    path: '/admin/feedbacks',
    breadcrumbText: 'Feedback',
    navigationText: 'Feedback',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/manage-homepage': {
    path: '/admin/manage-homepage',
    breadcrumbText: 'Landing Page & Support',
    navigationText: 'Landing Page & Support',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/finance-calendar': {
    path: '/admin/finance-calendar',
    breadcrumbText: 'Finance Calendar',
    navigationText: 'Finance Calendar',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/manage-team-page': {
    path: '/admin/manage-team-page',
    breadcrumbText: 'Team Settings',
    navigationText: 'Team Settings',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/logo-library': {
    path: '/admin/logo-library',
    breadcrumbText: 'Logo Library',
    navigationText: 'Logo Library',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/view-metadata': {
    path: '/admin/view-metadata',
    breadcrumbText: 'View Metadata',
    navigationText: 'App Metadata',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Developer',
    accessibleTo: DEV_LDAP_GROUPS
  },
  '/admin/kingpin-goals': {
    path: '/admin/kingpin-goals',
    breadcrumbText: 'Kingpin Goals',
    navigationText: 'Kingpin Goals',
    parent: '/admin',
    showInNavigation: true,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/mcp-servers': {
    path: '/admin/mcp-servers',
    breadcrumbText: 'MCP Servers',
    navigationText: 'MCP Servers',
    parent: '/admin',
    showInNavigation: false,
    navigationSection: 'Content',
    accessibleTo: ADMIN_LDAP_GROUPS
  },
  '/admin/ea-huddle': {
    path: '/admin/ea-huddle',
    breadcrumbText: 'Huddle+',
    navigationText: 'Huddle+',
    parent: '/ea-huddle',
    showInNavigation: false, // Accessed via the gear icon on the Huddle+ page
    navigationSection: 'org-tools',
    accessibleTo: HUDDLEPLUS_ADMIN_LDAP_GROUPS
  },
  '/admin/manage-mcp-server': {
    path: '/admin/manage-mcp-server',
    breadcrumbText: 'Manage MCP Server',
    navigationText: 'Manage MCP Server',
    parent: '/admin/mcp-servers',
    showInNavigation: false,
    accessibleTo: ADMIN_LDAP_GROUPS
  },

  // FinTech Ops routes - Operational Excellence
  '/operational-excellence': {
    path: '/operational-excellence',
    breadcrumbText: Titles.TITLE_OP_EX,
    navigationText: Titles.TITLE_OP_EX,
    parent: '/',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/oe-insights': {
    path: '/operational-excellence/oe-insights',
    breadcrumbText: Titles.TITLE_BUSINESS_REPORTING,
    navigationText: Titles.TITLE_BUSINESS_REPORTING,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/ticket-health': {
    path: '/operational-excellence/ticket-health',
    breadcrumbText: Titles.TITLE_TICKETS,
    navigationText: Titles.TITLE_TICKETS,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/user-metrics': {
    path: '/operational-excellence/user-metrics',
    breadcrumbText: Titles.TITLE_USER_METRICS,
    navigationText: Titles.TITLE_USER_METRICS,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/security-risks': {
    path: '/operational-excellence/security-risks',
    breadcrumbText: Titles.TITLE_SAS,
    navigationText: Titles.TITLE_SAS,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/policy-engine': {
    path: '/operational-excellence/policy-engine',
    breadcrumbText: Titles.TITLE_POLICY_ENGINE,
    navigationText: Titles.TITLE_POLICY_ENGINE,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/pipeline-health': {
    path: '/operational-excellence/pipeline-health',
    breadcrumbText: Titles.TITLE_DOGMA,
    navigationText: Titles.TITLE_DOGMA,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/risk-remediator': {
    path: '/operational-excellence/risk-remediator',
    breadcrumbText: Titles.TITLE_RISK_REMEDIATOR,
    navigationText: Titles.TITLE_RISK_REMEDIATOR,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/upgrade-medic': {
    path: '/operational-excellence/upgrade-medic',
    breadcrumbText: 'Upgrade Medic',
    navigationText: 'Upgrade Medic',
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/data-mapping': {
    path: '/operational-excellence/data-mapping',
    breadcrumbText: Titles.TITLE_OP_MAPPING,
    navigationText: Titles.TITLE_OP_MAPPING,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/cost-metrics': {
    path: '/operational-excellence/cost-metrics',
    breadcrumbText: Titles.TITLE_COST_METRICS,
    navigationText: Titles.TITLE_COST_METRICS,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/kpi-metrics': {
    path: '/operational-excellence/kpi-metrics',
    breadcrumbText: Titles.TITLE_OP_KPI_METRICS,
    navigationText: Titles.TITLE_OP_KPI_METRICS,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  '/operational-excellence/pipeline-oe-dashboard': {
    path: '/operational-excellence/pipeline-oe-dashboard',
    breadcrumbText: Titles.TITLE_PIPELINE_OE_DASHBOARD,
    navigationText: Titles.TITLE_PIPELINE_OE_DASHBOARD,
    parent: '/operational-excellence',
    showInNavigation: true,
    navigationSection: 'operational-excellence'
  },
  // Kingpin routes - Goals & Business Reviews
  // Note: These are external-only links (not React Router routes).
  // The `path` field contains the external URL since these entries
  // are only consumed by side navigation via getNavigationItemsBySection().
  '/kingpin/fgbs-goals': {
    path: KingPinSideNavLinks.DAS_FGBS_GOALS,
    breadcrumbText: 'DaS Finance FGBS Goals',
    navigationText: 'DaS Finance FGBS Goals',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'kingpin',
    external: true
  },
  '/kingpin/vp-goals': {
    path: KingPinSideNavLinks.DAS_FINANCE_VP_GOALS,
    breadcrumbText: 'DaS Finance VP Goals',
    navigationText: 'DaS Finance VP Goals',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'kingpin',
    external: true
  },
  '/kingpin/fintech-goals': {
    path: KingPinSideNavLinks.DAS_FINTECH_TEAM_GOALS,
    breadcrumbText: 'DaS FinTech Team Goals',
    navigationText: 'DaS FinTech Team Goals',
    parent: '/',
    showInNavigation: true,
    navigationSection: 'kingpin',
    external: true
  },

  // App-specific base routes
  '/app-announcements': {
    path: '/announcements',
    breadcrumbText: 'Announcements',
    navigationText: 'Announcements',
    showInNavigation: true,
    navigationSection: 'app-specific'
  },
  '/app-tutorials': {
    path: '/tutorials',
    breadcrumbText: 'Tutorials',
    navigationText: 'Tutorials',
    showInNavigation: true,
    navigationSection: 'app-specific'
  }
};

// Helper function to build breadcrumb chain
export const buildBreadcrumbChain = (path: string): BreadcrumbGroupProps.Item[] => {
  const breadcrumbs: BreadcrumbGroupProps.Item[] = [];
  const visited = new Set<string>();

  const addBreadcrumb = (currentPath: string) => {
    if (visited.has(currentPath)) return; // Prevent infinite loops
    visited.add(currentPath);

    const config = ROUTE_CONFIG[currentPath];
    if (!config) return;

    // Recursively add parent breadcrumbs first
    if (config.parent) {
      addBreadcrumb(config.parent);
    }

    breadcrumbs.push({
      text: config.breadcrumbText,
      href: config.path
    });
  };

  addBreadcrumb(path);
  return breadcrumbs;
};

// Helper function for dynamic routes (like /all-apps/:id)
export const buildDynamicBreadcrumbs = (basePath: string, dynamicText: string, dynamicHref?: string): BreadcrumbGroupProps.Item[] => {
  const baseBreadcrumbs = buildBreadcrumbChain(basePath);

  return [
    ...baseBreadcrumbs,
    {
      text: dynamicText,
      href: dynamicHref || '#'
    }
  ];
};

// Navigation Helper Functions

// Get routes for a specific navigation section
export const getNavigationItemsBySection = (section: string, userLDAPGroups?: string[], environment?: string): SideNavigationProps.Item[] => {
  return Object.values(ROUTE_CONFIG)
    .filter((route) => {
      // Must be in the specified section and show in navigation
      if (route.navigationSection !== section || !route.showInNavigation) {
        return false;
      }

      // Check environment restrictions
      if (route.environmentRestriction) {
        if (route.environmentRestriction === 'non-prod' && environment === 'prod') {
          return false;
        }
        if (route.environmentRestriction === 'prod' && environment !== 'prod') {
          return false;
        }
      }

      // Check access permissions
      if (route.accessibleTo && userLDAPGroups) {
        const hasAccess = route.accessibleTo.some((group) => userLDAPGroups.includes(group));
        if (!hasAccess) {
          return false;
        }
      }

      return true;
    })
    .map((route) => ({
      type: 'link' as const,
      text: route.navigationText || route.breadcrumbText,
      href: route.path,
      external: route.external || false
    }));
};

// Get FinSuite navigation items
export const getFinSuiteNavigationItems = (userLDAPGroups?: string[], environment?: string): SideNavigationProps.Item[] => {
  return getNavigationItemsBySection('finsuite', userLDAPGroups, environment);
};

// Get Operational Excellence navigation items (excluding the parent route)
export const getOperationalExcellenceNavigationItems = (): SideNavigationProps.Item[] => {
  return getNavigationItemsBySection('operational-excellence').filter((item) => {
    return item.type === 'link' && item.href !== '/operational-excellence';
  });
};

// Get Admin navigation items grouped by section
export const getAdminNavigationItems = (userLDAPGroups: string[]): SideNavigationProps.Item[] => {
  const items: SideNavigationProps.Item[] = [];

  // Add "New application" item first (special case)
  const newAppItems = getNavigationItemsBySection('admin-new-app', userLDAPGroups);
  items.push(...newAppItems);

  // Get all admin sections except the special ones
  const adminSections = ['Content', 'Developer'];

  adminSections.forEach((sectionName) => {
    const sectionItems = getNavigationItemsBySection(sectionName, userLDAPGroups);

    if (sectionItems.length > 0) {
      items.push({
        type: 'section',
        text: sectionName,
        items: sectionItems
      });
    }
  });

  return items;
};

// Get App-specific navigation items
export const getAppSpecificNavigationItems = (): SideNavigationProps.Item[] => {
  return getNavigationItemsBySection('app-specific');
};

// Get Operational Excellence group for expandable navigation
export const getOperationalExcellenceGroup = (): SideNavigationProps.Item => {
  const oeConfig = ROUTE_CONFIG['/operational-excellence'];
  const oeItems = getOperationalExcellenceNavigationItems();

  return {
    type: 'expandable-link-group',
    defaultExpanded: true,
    text: oeConfig.navigationText || oeConfig.breadcrumbText,
    href: oeConfig.path,
    items: oeItems
  };
};

// Helper to check if user has access to any routes in a section
export const hasAccessToSection = (section: string, userLDAPGroups: string[], environment?: string): boolean => {
  return getNavigationItemsBySection(section, userLDAPGroups, environment).length > 0;
};
